// Crisis Management Principles
export interface CrisisPrinciple {
  key: string;
  label: string;
}

export const CRISIS_PRINCIPLES: Record<string, string> = {
  SEE_THINK_ACT_REPEAT: "OODA: See → Think → Act → Repeat",
  RULE_OF_FIVE: "Rule of Five: What we know / What we don't know / Impacts / Priorities / Who does what by when",
  SITREP: "SITREP: At time / At place / What happened / What done / Next",
  FACT_VS_RUMOR: "Fact vs Rumor: Collect → Validate → Label → Decide",
  STAKEHOLDER_BALANCE: "Stakeholder Balance: People vs Services vs Reputation",
  ESCALATION_PATHS: "Escalation Paths: Who decides, when, how",
  COMMS_CADENCE: "Comms Cadence: Right info, right audience, right time",
  DECISION_LOG: "Decision Log: Decision, rationale, owner, deadline",
  RISK_TRADEOFFS: "Risk Tradeoffs: Make tradeoffs explicit and recorded",
  TASK_PRIORITISATION: "Task Prioritisation: Triage must/should/could",
  ROLE_CLARITY: "Role Clarity: Who leads what by when",
};

// Wizard Form Data
export interface WizardFormData {
  // Section 1: Context & Audience
  industry: "Health" | "Insurance" | "Retail" | "Finance" | "Government" | "Other";
  geography: string;
  audience: "Board" | "Crisis Management Team" | "Site-level Team" | "General Staff" | "Mixed";
  
  // Section 2: Scenario Configuration
  scenarioType: "Fire" | "Flood" | "Active Threat" | "Medical" | "Cyber" | "Supply Chain" | "Other";
  durationMinutes: 30 | 60 | 90;
  injectCount: 3 | 5 | 7;
  
  // Section 3: Principle Mapping
  selectedPrinciples: {
    order: number;
    principleKey: string;
    principleLabel: string;
  }[];
  
  // Section 4: Optional Configuration
  objectives: string[];
  riskDial: number; // 1-5 scale
  stakeholders: string[];
  complianceFrameworks: string[];
}

// Dropdown Options
export const INDUSTRY_OPTIONS = [
  "Health",
  "Insurance", 
  "Retail",
  "Finance",
  "Government",
  "Other"
] as const;

export const AUDIENCE_OPTIONS = [
  "Board",
  "Crisis Management Team",
  "Site-level Team", 
  "General Staff",
  "Mixed"
] as const;

export const SCENARIO_OPTIONS = [
  "Fire",
  "Flood",
  "Active Threat",
  "Medical", 
  "Cyber",
  "Supply Chain",
  "Other"
] as const;

export const DURATION_OPTIONS = [
  { label: "Short (30 min)", value: 30, injects: 3 },
  { label: "Standard (60 min)", value: 60, injects: 5 },
  { label: "Extended (90 min)", value: 90, injects: 7 }
] as const;

export const STAKEHOLDER_OPTIONS = [
  "Staff",
  "Customers", 
  "Regulators",
  "Media",
  "Board",
  "Suppliers"
] as const;

export const COMPLIANCE_OPTIONS = [
  "CPS230",
  "ISO22301",
  "AS/NZS 5050",
  "NIST Cybersecurity Framework"
] as const;

export const COMMON_OBJECTIVES = [
  "Test crisis communication processes",
  "Assess escalation procedures", 
  "Evaluate decision-making under pressure",
  "Practice stakeholder management",
  "Validate business continuity plans",
  "Test regulatory notification procedures"
] as const;

// Recommended Principle Orders
export const RECOMMENDED_PRINCIPLE_ORDERS: Record<number, string[]> = {
  1: ["SEE_THINK_ACT_REPEAT"],
  2: ["SEE_THINK_ACT_REPEAT", "STAKEHOLDER_BALANCE"],
  3: ["SEE_THINK_ACT_REPEAT", "STAKEHOLDER_BALANCE", "COMMS_CADENCE"],
  4: ["SEE_THINK_ACT_REPEAT", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "COMMS_CADENCE"],
  5: ["SEE_THINK_ACT_REPEAT", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "DECISION_LOG", "COMMS_CADENCE"],
  6: ["SEE_THINK_ACT_REPEAT", "SITREP", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "DECISION_LOG", "COMMS_CADENCE"],
  7: ["SEE_THINK_ACT_REPEAT", "SITREP", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "ESCALATION_PATHS", "DECISION_LOG", "COMMS_CADENCE"],
  8: ["SEE_THINK_ACT_REPEAT", "SITREP", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "ESCALATION_PATHS", "DECISION_LOG", "COMMS_CADENCE", "RISK_TRADEOFFS"],
  9: ["SEE_THINK_ACT_REPEAT", "SITREP", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "ESCALATION_PATHS", "DECISION_LOG", "COMMS_CADENCE", "RISK_TRADEOFFS", "TASK_PRIORITISATION"],
  10: ["SEE_THINK_ACT_REPEAT", "SITREP", "FACT_VS_RUMOR", "STAKEHOLDER_BALANCE", "ESCALATION_PATHS", "DECISION_LOG", "COMMS_CADENCE", "RISK_TRADEOFFS", "TASK_PRIORITISATION", "ROLE_CLARITY"]
};

